import { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Button, Drawer, Form, Input, Pagination, Select, Space, Table, Tag, Upload } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { apiPost } from '../api.js';
import { useGlobalStore } from '@digiwedge/global-state';
import { TableToolbar } from '../ui/TableToolbar.js';
import { useUrlPagination } from '../hooks/useUrlPagination.js';

type Row = {
  id: string;
  siteKey: string | null;
  name: string;
  domain: string | null;
  vendor: string | null;
  category: string;
  purpose: string | null;
  retention: string | null;
  reason: string | null;
  updatedAt: string;
};

export function CookieOverridesPage({
  registryUrl,
  siteKey,
}: {
  registryUrl: string;
  siteKey: string;
}) {
  const { message, modal } = App.useApp();
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const { page, pageSize, q, setPage, setPageSize } = useUrlPagination(50);
  const [scope, setScope] = useState<'all' | 'global' | 'site'>('site');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<Row | null>(null);

  const body = useMemo(() => {
    const offset = (page - 1) * pageSize;
    const b: Record<string, unknown> = { query: q, scope, limit: pageSize, offset };
    if (scope === 'site') b['site_key'] = siteKey;
    return b;
  }, [q, scope, page, pageSize, siteKey]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiPost<{ items: Row[]; total: number }>(
        `${registryUrl}/admin/cookies/overrides:list`,
        body,
      );
      setRows(res.items);
      setTotal(res.total);
    } catch (e) {
      message.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [registryUrl, body, message]);

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  const onPage = async (p: number, s: number) => {
    setPage(p);
    if (s !== pageSize) setPageSize(s);
  };

  const remove = (r: Row) => {
    modal.confirm({
      title: 'Delete override?',
      content: `${r.name}${r.domain ? ` @ ${r.domain}` : ''} (${r.siteKey || 'global'})`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await apiPost(`${registryUrl}/admin/cookies/overrides:delete`, {
            site_key: r.siteKey ?? undefined,
            name: r.name,
            domain: r.domain ?? undefined,
          });
          message.success('Override removed');
          await load();
        } catch (e) {
          message.error((e as Error).message);
        }
      },
    });
  };

  const edit = (r: Row) => setEditing(r);
  const closeEdit = () => setEditing(null);

  const columns: ColumnsType<Row> = [
    {
      title: 'Site',
      dataIndex: 'siteKey',
      key: 'siteKey',
      width: 140,
      render: (v) => v || <Tag>global</Tag>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (v, r) => (
        <div>
          <code>{v}</code>
          <div style={{ color: '#888', fontSize: 12 }}>{r.domain || '(host-only)'}</div>
        </div>
      ),
    },
    { title: 'Vendor', dataIndex: 'vendor', key: 'vendor', render: (v) => v || '—' },
    { title: 'Category', dataIndex: 'category', key: 'category', render: (v) => <Tag>{v}</Tag> },
    { title: 'Retention', dataIndex: 'retention', key: 'retention', width: 140 },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 200,
      render: (v) => new Date(v).toLocaleString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_: unknown, r) => (
        <Space>
          <Button size="small" onClick={() => edit(r)}>
            Edit
          </Button>
          <Button size="small" danger onClick={() => remove(r)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <TableToolbar
        placeholder="Search name or domain"
        extra={
          <>
            <Select
              value={scope}
              onChange={(v) => setScope(v)}
              options={[
                { value: 'site', label: 'This site' },
                { value: 'global', label: 'Global' },
                { value: 'all', label: 'All' },
              ]}
              style={{ width: 140 }}
            />
            <Space>
              <Button onClick={() => load()} loading={loading}>
                Refresh
              </Button>
              <Button
                onClick={async () => {
                  try {
                    const r = await fetch(`${registryUrl}/admin/cookies/overrides:export`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        ...(useGlobalStore.getState().token
                          ? { Authorization: `Bearer ${useGlobalStore.getState().token}` }
                          : {}),
                      },
                      body: JSON.stringify({
                        scope,
                        site_key: scope === 'site' ? siteKey : undefined,
                        query: q,
                      }),
                    });
                    const blob = await r.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'cookie_overrides.csv';
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch (e) {
                    message.error((e as Error).message);
                  }
                }}
              >
                Export CSV
              </Button>
              <Upload
                accept=".csv"
                showUploadList={false}
                beforeUpload={async (file) => {
                  try {
                    const text = await file.text();
                    await apiPost(`${registryUrl}/admin/cookies/overrides:import`, {
                      csv: text,
                      default_site_key: scope === 'site' ? siteKey : undefined,
                    });
                    message.success('Import complete');
                    await load();
                  } catch (e) {
                    message.error((e as Error).message);
                  }
                  return false;
                }}
              >
                <Button>Import CSV</Button>
              </Upload>
              <Button
                onClick={() =>
                  setEditing({
                    id: '',
                    siteKey: scope === 'site' ? siteKey : null,
                    name: '',
                    domain: null,
                    vendor: null,
                    category: 'functional',
                    purpose: null,
                    retention: null,
                    reason: null,
                    updatedAt: new Date().toISOString(),
                  })
                }
              >
                New override
              </Button>
            </Space>
          </>
        }
      />

      <Table<Row>
        rowKey={(r) => r.id}
        dataSource={rows}
        columns={columns}
        loading={loading}
        pagination={false}
        size="middle"
      />

      <Pagination
        current={page}
        total={total}
        pageSize={pageSize}
        showSizeChanger
        pageSizeOptions={[25, 50, 100]}
        onChange={onPage}
        style={{ marginLeft: 'auto' }}
      />

      <Drawer
        title={editing?.id ? 'Edit override' : 'New override'}
        open={!!editing}
        onClose={closeEdit}
        width={480}
      >
        {editing && (
          <Form
            layout="vertical"
            initialValues={{
              siteKey: editing.siteKey || (scope === 'site' ? siteKey : ''),
              name: editing.name,
              domain: editing.domain || '',
              vendor: editing.vendor || '',
              category: editing.category,
              purpose: editing.purpose || '',
              retention: editing.retention || '',
              reason: editing.reason || '',
            }}
            onFinish={async (vals) => {
              try {
                await apiPost(`${registryUrl}/admin/cookies/overrides`, {
                  site_key: vals.siteKey || undefined,
                  name: vals.name,
                  domain: vals.domain || undefined,
                  vendor: vals.vendor || undefined,
                  category: vals.category,
                  purpose: vals.purpose || undefined,
                  retention: vals.retention || undefined,
                  reason: vals.reason || undefined,
                });
                message.success('Saved');
                setEditing(null);
                await load();
              } catch (e) {
                message.error((e as Error).message);
              }
            }}
          >
            <Form.Item label="Site key (blank for global)" name="siteKey">
              <Input placeholder="e.g. DEV_SITE_KEY" />
            </Form.Item>
            <Form.Item label="Cookie name" name="name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item label="Domain (optional)" name="domain">
              <Input />
            </Form.Item>
            <Form.Item label="Vendor" name="vendor">
              <Input />
            </Form.Item>
            <Form.Item label="Category" name="category" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: 'essential', label: 'essential' },
                  { value: 'functional', label: 'functional' },
                  { value: 'analytics', label: 'analytics' },
                  { value: 'advertising', label: 'advertising' },
                  { value: 'uncategorized', label: 'uncategorized' },
                ]}
              />
            </Form.Item>
            <Form.Item label="Purpose" name="purpose">
              <Input />
            </Form.Item>
            <Form.Item label="Retention" name="retention">
              <Input />
            </Form.Item>
            <Form.Item label="Reason (audit)" name="reason">
              <Input />
            </Form.Item>
            <Space>
              <Button htmlType="submit" type="primary">
                Save
              </Button>
              <Button onClick={closeEdit}>Cancel</Button>
            </Space>
          </Form>
        )}
      </Drawer>
    </div>
  );
}
